import { supabase, getSupabaseWithJWT } from '@/integrations/supabase/client';
import { getCurrentProfileIdCached } from '@/services/profileService';
import { organizationService } from '@/services/organizationService';
import type { Organization, OrgRole } from '@/types/organization';
import {
  DEFAULT_WW_SEASON_CODE,
  type WelcomeWeekEffectiveEvent,
  type WelcomeWeekAdminCalendarSource,
  type WelcomeWeekGroup,
  type WelcomeWeekGroupAdmin,
  type WelcomeWeekGroupFeed,
  type WelcomeWeekGroupInvitation,
  type WelcomeWeekMasterEvent,
  type WelcomeWeekOverride,
  type WelcomeWeekPublicOverview,
  type WelcomeWeekSeason,
} from '@/types/welcomeWeek';

interface SupabaseResult<T = unknown> {
  data: T | null;
  error: Error | { message?: string } | null;
}

interface SupabaseQueryBuilder<T = unknown> extends PromiseLike<SupabaseResult<T>> {
  delete(): SupabaseQueryBuilder<T>;
  eq(column: string, value: string | number | boolean | null): SupabaseQueryBuilder<T>;
  in(column: string, values: readonly unknown[]): SupabaseQueryBuilder<T>;
  insert(values: unknown): SupabaseQueryBuilder<T>;
  maybeSingle(): Promise<SupabaseResult<T>>;
  or(filters: string): SupabaseQueryBuilder<T>;
  order(column: string, options?: { ascending?: boolean }): SupabaseQueryBuilder<T>;
  select(columns?: string): SupabaseQueryBuilder<T>;
  single(): Promise<SupabaseResult<T>>;
  update(values: unknown): SupabaseQueryBuilder<T>;
  upsert(values: unknown, options?: Record<string, unknown>): SupabaseQueryBuilder<T>;
}

interface UntypedSupabaseClient {
  from<T = unknown>(table: string): SupabaseQueryBuilder<T>;
  rpc<T = unknown>(fn: string, args?: Record<string, unknown>): Promise<SupabaseResult<T>>;
}

interface MyAdminGroupRow {
  ww_groups: WelcomeWeekGroup | null;
}

const WW_GROUP_SELECT_COLUMNS = 'id, organization_id, season_id, code, name, slug, feed_token, created_by, created_at, updated_at';
const WW_GROUP_EMBED_SELECT_COLUMNS = 'id,organization_id,season_id,code,name,slug,feed_token,created_by,created_at,updated_at';

function untypedSupabase(client: unknown = supabase): UntypedSupabaseClient {
  return client as UntypedSupabaseClient;
}

function slugify(value: string): string {
  const slug = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'group';
}

function inferSeasonYear(code: string): { year: number; season: 'h' | 'v' } {
  const match = /^(\d{2})([hv])$/.exec(code);
  if (!match) return { year: new Date().getFullYear(), season: 'h' };
  return { year: 2000 + Number(match[1]), season: match[2] as 'h' | 'v' };
}

function normalizeNullable(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function unwrapJson<T>(data: unknown): T | null {
  if (!data || (typeof data === 'object' && Object.keys(data as Record<string, unknown>).length === 0)) {
    return null;
  }
  return data as T;
}

export interface WelcomeWeekAdminContext {
  organization: Organization;
  role: OrgRole;
  season: WelcomeWeekSeason | null;
}

export interface SaveOverrideInput {
  organization_id: string;
  season_id: string;
  group_id: string;
  master_event_id: string;
  override_title?: string | null;
  override_description?: string | null;
  override_location_name?: string | null;
  override_location_address?: string | null;
  override_location_url?: string | null;
  override_starts_at?: string | null;
  override_ends_at?: string | null;
  notes?: string | null;
  is_hidden?: boolean;
}

export const welcomeWeekService = {
  slugify,

  async getPublicOverview(
    orgSlug: string,
    seasonCode: string = DEFAULT_WW_SEASON_CODE
  ): Promise<WelcomeWeekPublicOverview | null> {
    const { data, error } = await untypedSupabase()
      .rpc<WelcomeWeekPublicOverview>('get_public_ww_overview', { org_slug: orgSlug, season_code: seasonCode });
    if (error) throw error;
    return unwrapJson<WelcomeWeekPublicOverview>(data);
  },

  async getGroupFeed(feedToken: string): Promise<WelcomeWeekGroupFeed | null> {
    const { data, error } = await untypedSupabase()
      .rpc<WelcomeWeekGroupFeed>('get_group_feed', { p_feed_token: feedToken });
    if (error) throw error;
    return unwrapJson<WelcomeWeekGroupFeed>(data);
  },

  async getPublicGroupFeed(
    orgSlug: string,
    groupCodeOrSlug: string,
    seasonCode: string = DEFAULT_WW_SEASON_CODE
  ): Promise<WelcomeWeekGroupFeed | null> {
    const overview = await this.getPublicOverview(orgSlug, seasonCode);
    const group = overview?.groups?.find((item) =>
      item.code.toLowerCase() === groupCodeOrSlug.toLowerCase()
      || item.slug.toLowerCase() === groupCodeOrSlug.toLowerCase()
    );
    if (!group) return null;
    return this.getGroupFeed(group.feed_token);
  },

  // Mirror the CalOne master into ww_master_events so the group feed (and the
  // mobile-friendly list it drives) reflects edits made on the CalOne side.
  // Best-effort: the CalOne master/ICS stay authoritative regardless, so a
  // failure here must never block the action that triggered it. The edge
  // function is verify_jwt=false, so the plain anon client is enough (pin-only
  // group admins aren't logged in).
  async syncWwMaster(orgSlug: string | undefined, seasonCode: string | undefined): Promise<void> {
    if (!orgSlug || !seasonCode) return;
    try {
      await supabase.functions.invoke('sync-ww-master', {
        body: { orgSlug: orgSlug.toLowerCase(), season: seasonCode },
      });
    } catch (error) {
      console.warn('[ww] syncWwMaster failed (non-fatal)', error);
    }
  },

  async getAdminContext(
    orgSlug: string,
    seasonCode: string = DEFAULT_WW_SEASON_CODE
  ): Promise<WelcomeWeekAdminContext> {
    const [organizations, memberships] = await Promise.all([
      organizationService.listMyOrganizations(),
      organizationService.myMemberships(),
    ]);

    const organization = organizations.find((org) => org.slug.toLowerCase() === orgSlug.toLowerCase());
    if (!organization) {
      throw new Error('Organization not found or you are not a member.');
    }

    const membership = memberships.find((item) => item.organization_id === organization.id);
    if (!membership) {
      throw new Error('You are not a member of this organization.');
    }

    const season = await this.getSeasonByCode(organization.id, seasonCode);
    return { organization, role: membership.role, season };
  },

  async getSeasonByCode(organizationId: string, code: string): Promise<WelcomeWeekSeason | null> {
    const client = await getSupabaseWithJWT();
    const { data, error } = await untypedSupabase(client)
      .from<WelcomeWeekSeason>('ww_seasons')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('code', code)
      .maybeSingle();
    if (error) throw error;
    return (data as WelcomeWeekSeason) || null;
  },

  async ensureSeason(
    organizationId: string,
    code: string = DEFAULT_WW_SEASON_CODE,
    name = 'Omega Fadderuke 2026'
  ): Promise<WelcomeWeekSeason> {
    const client = await getSupabaseWithJWT();
    const { year, season } = inferSeasonYear(code);
    const { data, error } = await untypedSupabase(client)
      .from<WelcomeWeekSeason>('ww_seasons')
      .upsert(
        {
          organization_id: organizationId,
          code,
          name,
          year,
          season,
          is_active: true,
        },
        { onConflict: 'organization_id,code' }
      )
      .select('*')
      .single();
    if (error || !data) throw error || new Error('Failed to create welcome-week season.');
    return data as WelcomeWeekSeason;
  },

  async listMasterEvents(seasonId: string): Promise<WelcomeWeekMasterEvent[]> {
    const client = await getSupabaseWithJWT();
    const { data, error } = await untypedSupabase(client)
      .from<WelcomeWeekMasterEvent[]>('ww_master_events')
      .select('*')
      .eq('season_id', seasonId)
      .order('sort_order', { ascending: true })
      .order('starts_at', { ascending: true });
    if (error) throw error;
    return (data || []) as WelcomeWeekMasterEvent[];
  },

  async saveMasterEvent(input: Partial<WelcomeWeekMasterEvent> & {
    organization_id: string;
    season_id: string;
    title: string;
    starts_at: string;
    ends_at: string;
  }): Promise<WelcomeWeekMasterEvent> {
    const client = await getSupabaseWithJWT();
    const payload = {
      ...input,
      description: normalizeNullable(input.description),
      location_name: normalizeNullable(input.location_name),
      location_address: normalizeNullable(input.location_address),
      location_url: normalizeNullable(input.location_url),
      category: input.category || 'time',
    };

    const wwMasterEvents = untypedSupabase(client).from<WelcomeWeekMasterEvent>('ww_master_events');
    const query = input.id
      ? wwMasterEvents.update(payload).eq('id', input.id)
      : wwMasterEvents.insert(payload);

    const { data, error } = await query.select('*').single();
    if (error || !data) throw error || new Error('Failed to save master event.');
    return data as WelcomeWeekMasterEvent;
  },

  async deleteMasterEvent(eventId: string): Promise<void> {
    const client = await getSupabaseWithJWT();
    const { error } = await untypedSupabase(client)
      .from('ww_master_events')
      .delete()
      .eq('id', eventId);
    if (error) throw error;
  },

  async listGroups(seasonId: string): Promise<WelcomeWeekGroup[]> {
    const client = await getSupabaseWithJWT();
    const { data: groups, error } = await untypedSupabase(client)
      .from<WelcomeWeekGroup[]>('ww_groups')
      .select(WW_GROUP_SELECT_COLUMNS)
      .eq('season_id', seasonId);
    if (error) throw error;

    let groupRows = (groups || []) as WelcomeWeekGroup[];
    if (groupRows.length === 0) return [];
    
    groupRows.sort((a, b) => {
      if (a.code.toLowerCase() === 'pi') return -1;
      if (b.code.toLowerCase() === 'pi') return 1;
      return a.code.localeCompare(b.code);
    });

    const groupIds = groupRows.map((group) => group.id);
    const { data: adminRows, error: adminsError } = await (client as any).rpc('get_ww_group_admin_profiles', {
      p_season_id: seasonId,
    });
    if (adminsError) throw adminsError;

    const toAdminProfile = (row: any) => row.profile_id ? {
      id: row.profile_id,
      display_name: row.display_name,
      email: row.email,
      avatar_url: row.avatar_url,
    } : null;

    const admins = (adminRows || []).map((row: any) => ({
      group_id: row.group_id,
      user_id: row.user_id,
      profiles: toAdminProfile(row),
    })) as WelcomeWeekGroupAdmin[];

    const orgMemberProfiles = new Map<string, NonNullable<WelcomeWeekGroupAdmin['profiles']>>();
    const { data: orgMemberRows } = await (client as any).rpc('get_organization_members_with_profiles', {
      p_org_id: groupRows[0].organization_id,
    });
    (orgMemberRows || []).forEach((row: any) => {
      if (!row.profile_id) return;
      orgMemberProfiles.set(row.user_id, toAdminProfile(row)!);
    });

    const missingProfileIds = [
      ...new Set(
        admins
          .filter((admin) => !admin.profiles && admin.user_id)
          .map((admin) => admin.user_id)
      ),
    ];

    if (missingProfileIds.length > 0) {
      const { data: safeProfiles } = await client
        .from('profiles')
        .select('id, display_name, avatar_url, username')
        .in('id', missingProfileIds);

      (safeProfiles || []).forEach((profile: any) => {
        orgMemberProfiles.set(profile.id, {
          id: profile.id,
          display_name: profile.display_name || profile.username || null,
          email: null,
          avatar_url: profile.avatar_url,
        });
      });
    }

    const adminsByGroup = new Map<string, WelcomeWeekGroup['admins']>();
    admins
      .filter((admin) => groupIds.includes(admin.group_id))
      .forEach((admin) => {
        if (!admin.profiles) {
          admin.profiles = orgMemberProfiles.get(admin.user_id) || null;
        }
        const list = adminsByGroup.get(admin.group_id) || [];
        list.push(admin);
        adminsByGroup.set(admin.group_id, list);
      });

    const invitationsByGroup = await this.listGroupInvitations(groupIds);

    // admin_invite_token is not exposed via table SELECT — fetch through
    // SECURITY DEFINER RPC that gates on org owner/admin role.
    const tokensByGroup = new Map<string, string>();
    const { data: tokenRows } = await (client as any).rpc(
      'get_ww_group_admin_invite_tokens',
      { p_season_id: seasonId },
    );
    (tokenRows || []).forEach((row: { group_id: string; admin_invite_token: string }) => {
      if (row?.group_id && row?.admin_invite_token) {
        tokensByGroup.set(row.group_id, row.admin_invite_token);
      }
    });

    return groupRows.map((group) => ({
      ...group,
      admin_invite_token: tokensByGroup.get(group.id) ?? group.admin_invite_token,
      admins: adminsByGroup.get(group.id) || [],
      pendingInvitations: invitationsByGroup.get(group.id) || [],
    }));
  },

  async listGroupInvitations(groupIds: string[]): Promise<Map<string, WelcomeWeekGroupInvitation[]>> {
    const byGroup = new Map<string, WelcomeWeekGroupInvitation[]>();
    if (groupIds.length === 0) return byGroup;

    const client = await getSupabaseWithJWT();
    const { data, error } = await untypedSupabase(client)
      .from<Array<{
        invitation_id: string;
        group_id: string;
        consumed_at: string | null;
        organization_invitations: {
          email: string;
          accepted_at: string | null;
          expires_at: string | null;
          created_at: string | null;
        } | null;
      }>>('ww_group_admin_invitations')
      .select('invitation_id,group_id,consumed_at,organization_invitations(email,accepted_at,expires_at,created_at)')
      .in('group_id', groupIds);
    if (error) throw error;

    (data || []).forEach((row) => {
      const invite = row.organization_invitations;
      // Skip already-accepted invitations: those people show up as group admins instead.
      if (!invite || invite.accepted_at || row.consumed_at) return;
      const list = byGroup.get(row.group_id) || [];
      list.push({
        invitation_id: row.invitation_id,
        group_id: row.group_id,
        email: invite.email,
        created_at: invite.created_at,
        expires_at: invite.expires_at,
        accepted_at: invite.accepted_at,
        consumed_at: row.consumed_at,
      });
      byGroup.set(row.group_id, list);
    });

    byGroup.forEach((list) =>
      list.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || '')),
    );
    return byGroup;
  },

  async createGroup(input: {
    organization_id: string;
    season_id: string;
    code: string;
    name: string;
    slug?: string;
  }): Promise<WelcomeWeekGroup> {
    const client = await getSupabaseWithJWT();
    const profileId = await getCurrentProfileIdCached();
    const code = input.code.trim().toUpperCase();
    const { data, error } = await untypedSupabase(client)
      .from<WelcomeWeekGroup>('ww_groups')
      .insert({
        organization_id: input.organization_id,
        season_id: input.season_id,
        code,
        name: input.name.trim(),
        slug: input.slug ? slugify(input.slug) : slugify(code),
        created_by: profileId,
      })
      .select(WW_GROUP_SELECT_COLUMNS)
      .single();
    if (error || !data) throw error || new Error('Failed to create group.');
    return data as WelcomeWeekGroup;
  },

  async updateGroup(
    groupId: string,
    patch: { name?: string; code?: string; slug?: string },
  ): Promise<WelcomeWeekGroup> {
    const client = await getSupabaseWithJWT();
    const update: Record<string, string> = {};
    if (patch.name !== undefined) update.name = patch.name.trim();
    if (patch.code !== undefined) update.code = patch.code.trim().toUpperCase();
    if (patch.slug !== undefined) update.slug = slugify(patch.slug);
    if (Object.keys(update).length === 0) throw new Error('Nothing to update.');
    const { data, error } = await untypedSupabase(client)
      .from<WelcomeWeekGroup>('ww_groups')
      .update(update)
      .eq('id', groupId)
      .select(WW_GROUP_SELECT_COLUMNS)
      .single();
    if (error || !data) throw error || new Error('Failed to update group.');
    return data as WelcomeWeekGroup;
  },

  async removeGroupAdmin(groupId: string, userId: string): Promise<void> {
    const client = await getSupabaseWithJWT();
    const { error } = await untypedSupabase(client)
      .from('ww_group_admins')
      .delete()
      .eq('group_id', groupId)
      .eq('user_id', userId);
    if (error) throw error;
  },

  async revokeGroupInvitation(invitationId: string): Promise<void> {
    const client = await getSupabaseWithJWT();
    // The bridge row references organization_invitations(id) on delete cascade,
    // so deleting the invitation also clears the ww_group_admin_invitations row.
    const { error } = await untypedSupabase(client)
      .from('organization_invitations')
      .delete()
      .eq('id', invitationId);
    if (error) throw error;
  },

  async inviteGroupAdmin(group: WelcomeWeekGroup, email: string): Promise<void> {
    const client = await getSupabaseWithJWT();
    const profileId = await getCurrentProfileIdCached();
    const token = crypto.randomUUID();

    const { data: invitation, error: inviteError } = await untypedSupabase(client)
      .from<{ id: string }>('organization_invitations')
      .insert({
        organization_id: group.organization_id,
        email: email.trim(),
        role: 'member',
        token,
        invited_by: profileId,
      })
      .select('*')
      .single();
    if (inviteError || !invitation) throw inviteError || new Error('Failed to create invitation.');

    const { error: bridgeError } = await untypedSupabase(client)
      .from('ww_group_admin_invitations')
      .insert({
        invitation_id: invitation.id,
        organization_id: group.organization_id,
        season_id: group.season_id,
        group_id: group.id,
        created_by: profileId,
      });
    if (bridgeError) throw bridgeError;

    await this.sendGroupInvitationEmail(group, email.trim(), token);
  },

  async searchGroupAdminCandidates(groupId: string, query: string): Promise<{ id: string; display_name: string | null; avatar_url: string | null }[]> {
    const q = (query || '').trim();
    if (q.length < 2) return [];
    const client = await getSupabaseWithJWT();
    const { data, error } = await (client as any).rpc('search_group_admin_candidates', {
      p_query: q,
      p_group_id: groupId,
      p_limit: 8,
    });
    if (error) throw error;
    return (data || []) as any[];
  },

  /**
   * Returns the CalOne fullview edit pins the current user may use, gated
   * server-side (owner/admin -> all groups + master; group-admin -> own group).
   * Pins live in a locked-down Supabase table; only this RPC can read them.
   */
  async getMyEditAccess(
    orgSlug: string,
    seasonCode: string,
  ): Promise<{ masterPin: string | null; groupPins: Record<string, string> }> {
    const client = await getSupabaseWithJWT();
    const { data, error } = await (client as any).rpc('get_my_ww_edit_access', {
      p_org_slug: orgSlug,
      p_season_code: seasonCode,
    });
    if (error) throw error;
    const groupPins: Record<string, string> = {};
    for (const g of (data?.groups || []) as { code?: string; slug?: string; pin?: string }[]) {
      if (g?.pin) {
        if (g.code) groupPins[g.code.toLowerCase()] = g.pin;
        if (g.slug) groupPins[g.slug.toLowerCase()] = g.pin;
      }
    }
    return { masterPin: data?.master_pin ?? null, groupPins };
  },

  /**
   * Returns source-calendar metadata for an owner/admin. The public overview
   * deliberately exposes labels only; CalOne edit usernames and stems stay in
   * this authenticated RPC response.
   */
  async getMyCalendarSources(
    orgSlug: string,
    seasonCode: string,
    calendarSlug?: string,
  ): Promise<WelcomeWeekAdminCalendarSource[]> {
    const client = await getSupabaseWithJWT();
    const { data, error } = await (client as any).rpc('get_my_ww_calendar_sources', {
      p_org_slug: orgSlug,
      p_season_code: seasonCode,
      p_calendar_slug: calendarSlug || null,
    });
    if (error) throw error;
    return Array.isArray(data) ? data as WelcomeWeekAdminCalendarSource[] : [];
  },

  async addExistingUserAsGroupAdmin(groupId: string, userId: string): Promise<void> {
    const client = await getSupabaseWithJWT();
    const { data, error } = await (client as any).rpc('add_existing_user_as_group_admin', {
      p_group_id: groupId,
      p_user_id: userId,
    });
    if (error) throw error;
    if (!data?.success) throw new Error(data?.error || 'Failed to add group admin');
  },

  async acceptGroupAdminInvite(token: string): Promise<{ org_slug: string; group_slug: string; group_code: string }> {
    const client = await getSupabaseWithJWT();
    const { data, error } = await (client as any).rpc('accept_ww_group_admin_invite', {
      p_token: token,
    });
    if (error) {
      const msg = error.message || 'Could not accept invite.';
      if (msg.includes('auth required')) throw new Error('You need to be signed in to accept this invite.');
      if (msg.includes('invalid token')) throw new Error('This invite link is invalid or has been rotated.');
      throw error;
    }
    const result = Array.isArray(data) ? data[0] : data;
    if (result?.success === false) {
      const code = result?.error || 'unknown_error';
      const msg =
        code === 'not_authenticated'
          ? 'You need to be signed in to accept this invite.'
          : code === 'invalid_token'
          ? 'This invite link is invalid or has been rotated.'
          : code;
      throw new Error(msg);
    }
    if (!result?.org_slug || !result?.group_slug || !result?.group_code) {
      throw new Error('Could not accept invite.');
    }
    return { org_slug: result.org_slug, group_slug: result.group_slug, group_code: result.group_code };
  },

  async sendGroupInvitationEmail(group: WelcomeWeekGroup, email: string, token: string): Promise<void> {
    const org = await organizationService.getOrganization(group.organization_id);
    if (!org) throw new Error('Organization not found.');

    const inviteUrl = `${window.location.origin}/o/${org.slug}/invite/${token}`;
    const subject = `You're invited to manage ${group.name} for ${org.name}`;
    const content = `
      <div style="max-width: 600px; margin: 0 auto; padding: 20px; font-family: Arial, sans-serif;">
        <h1 style="color: #333; text-align: center;">Welcome-week group access</h1>
        <p>Hi there,</p>
        <p>You've been invited to manage <strong>${group.name}</strong> (${group.code}) for <strong>${org.name}</strong> on CalOne Events.</p>
        <div style="text-align: center; margin: 30px 0;">
          <a href="${inviteUrl}" style="background-color: #007bff; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block;">Accept Invitation</a>
        </div>
        <p>Or copy and paste this link in your browser:</p>
        <p style="word-break: break-all; color: #666;">${inviteUrl}</p>
        <p>Best regards,<br>The CalOne Events Team</p>
      </div>
    `;

    const response = await fetch('https://v1.calone.com/lovableapi/send-caloneevents-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify({
        to: [email],
        subject,
        content,
      }),
    });

    if (!response.ok) {
      const details = await response.json().catch(() => ({}));
      throw new Error(`Email API request failed: ${response.status} ${response.statusText}. ${details.error || ''}`);
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(`Email sending failed: ${result.error || 'Unknown error'}`);
    }
  },

  async listMyAdminGroups(organizationId: string, seasonId?: string): Promise<WelcomeWeekGroup[]> {
    const client = await getSupabaseWithJWT();
    const profileId = await getCurrentProfileIdCached();
    if (!profileId) return [];

    let query = untypedSupabase(client)
      .from<MyAdminGroupRow[]>('ww_group_admins')
      .select(`ww_groups!ww_group_admins_group_id_organization_id_season_id_fkey!inner(${WW_GROUP_EMBED_SELECT_COLUMNS})`)
      .eq('organization_id', organizationId)
      .eq('user_id', profileId);
    if (seasonId) query = query.eq('season_id', seasonId);

    const { data, error } = await query;
    if (error) throw error;
    return (data || [])
      .map((row) => row.ww_groups)
      .filter(Boolean) as WelcomeWeekGroup[];
  },

  async getGroupByCode(seasonId: string, codeOrSlug: string): Promise<WelcomeWeekGroup | null> {
    const client = await getSupabaseWithJWT();
    const normalized = codeOrSlug.toLowerCase();
    const { data, error } = await untypedSupabase(client)
      .from<WelcomeWeekGroup>('ww_groups')
      .select(WW_GROUP_SELECT_COLUMNS)
      .eq('season_id', seasonId)
      .or(`code.ilike.${normalized},slug.ilike.${normalized}`)
      .maybeSingle();
    if (error) throw error;
    return (data as WelcomeWeekGroup) || null;
  },

  async listOverrides(groupId: string): Promise<WelcomeWeekOverride[]> {
    const client = await getSupabaseWithJWT();
    const { data, error } = await untypedSupabase(client)
      .from<WelcomeWeekOverride[]>('ww_group_overrides')
      .select('*')
      .eq('group_id', groupId);
    if (error) throw error;
    return (data || []) as WelcomeWeekOverride[];
  },

  async saveOverride(input: SaveOverrideInput): Promise<WelcomeWeekOverride> {
    const client = await getSupabaseWithJWT();
    const payload = {
      ...input,
      override_title: normalizeNullable(input.override_title),
      override_description: normalizeNullable(input.override_description),
      override_location_name: normalizeNullable(input.override_location_name),
      override_location_address: normalizeNullable(input.override_location_address),
      override_location_url: normalizeNullable(input.override_location_url),
      override_starts_at: input.override_starts_at || null,
      override_ends_at: input.override_ends_at || null,
      notes: normalizeNullable(input.notes),
      is_hidden: input.is_hidden ?? false,
    };

    const { data, error } = await untypedSupabase(client)
      .from<WelcomeWeekOverride>('ww_group_overrides')
      .upsert(payload, { onConflict: 'group_id,master_event_id' })
      .select('*')
      .single();
    if (error || !data) throw error || new Error('Failed to save override.');
    return data as WelcomeWeekOverride;
  },

  async clearOverride(overrideId: string): Promise<void> {
    const client = await getSupabaseWithJWT();
    const { error } = await untypedSupabase(client)
      .from('ww_group_overrides')
      .delete()
      .eq('id', overrideId);
    if (error) throw error;
  },

  effectiveMasterEventsFromOverview(overview: WelcomeWeekPublicOverview | null): WelcomeWeekEffectiveEvent[] {
    return overview?.master_events || [];
  },
};
